import { useCallback, useState } from "react";
import useAdminLoad from "../../../hooks/useAdminLoad";
import * as crmApi from "../api/crmApi";

/**
 * The Team block (`GET /api/crm/team`, owner-only — spec "Team": invite by email, list,
 * resend, remove). The owner's own row is never part of this list (`tenant_members` v1
 * writes helper rows only, contract C3.01): the page renders it separately from the
 * signed-in identity, since only the owner can ever reach this screen (`RoleGate`).
 *
 * `invite`/`resend`/`remove` all re-read the list rather than patch it in place — the
 * roster is small (a practice's own team) and a re-read is one request, simpler than
 * reconciling a partial server answer (`inviteTeamMember` returns the new row, but
 * `removeTeamMember`/`resendTeamInvite` answer only `{ok}`).
 */
export default function useTeam() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    const page = await crmApi.listTeam();
    setRows(Array.isArray(page?.rows) ? page.rows : []);
  }, []);
  const { loading, loadError, reload } = useAdminLoad(load);

  const invite = useCallback(async (email) => {
    const saved = await crmApi.inviteTeamMember(email);
    await reload();
    return saved;
  }, [reload]);

  const resend = useCallback(async (id) => {
    await crmApi.resendTeamInvite(id);
  }, []);

  const remove = useCallback(async (id) => {
    await crmApi.removeTeamMember(id);
    await reload();
  }, [reload]);

  return { rows, loading, loadError, reload, invite, resend, remove };
}
